import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { createStructuredSelector } from 'reselect';
import connect from 'react-redux/es/connect/connect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GuestInformation from './components';
import HostBookingOptions from './components/HostBookingOptions';
import ErrorModal from 'components/ErrorModal';
import SpinnerContainer from '../DateSelection/components/styled/SpinnerContainer';
import {
  propertySelector,
  submittedAccountSelector,
  isAdminSelector,
  isWaitListBookingSelector
} from '../DateSelection/selectors';
import { bookingSourceSelector } from 'routes/selectors';
import { guestInfoSelector } from 'routes/ReviewAndSubmit/selectors';
import { selectedRoomSelector } from 'routes/ChooseAccommodation/selectors';
import {
  PROPERTY_CODES,
  US_STATES_OPTIONS,
  BOOKING_ENGINE_ON_SCROLL
} from '../../utils/constants';
import { HOST_BOOKING } from 'utils/routeUtils';
import fragments from 'utils/graphql/fragments';

const QUERY_GET_PROFILE = gql`
  query GetHotelProfile($input: GetHotelProfile!) {
    hotelProfile(input: $input) {
      ...HotelProfileFields
    }
  }
  ${fragments.HotelProfileFields}
`;

function GuestInformationContainer({
  submittedAccount,
  property,
  isAdmin,
  isWaitListBooking,
  bookingSource,
  ...props
}) {
  const [page, setPage] = useState(null);

  useEffect(() => {
    function handleMessageEvent(event) {
      if (event.data.type === BOOKING_ENGINE_ON_SCROLL) {
        setPage({
          scrollY: event.data.payload.scrollY,
          height:
            window.screen.availHeight - 2 * event.data.payload.headerOffset
        });
      }
    }

    window.addEventListener('message', handleMessageEvent);
    return () => window.removeEventListener('message', handleMessageEvent);
  });

  const skipQuery = !submittedAccount || !submittedAccount.accountNumber;
  const input = {
    patronAccountNumber: submittedAccount.accountNumber,
    propertyCode: PROPERTY_CODES[property]
  };

  if (!isAdmin && submittedAccount.accountPin) {
    input.pin = submittedAccount.accountPin;
  }

  return (
    <Query query={QUERY_GET_PROFILE} variables={{ input }} skip={skipQuery}>
      {({ loading, error, data }) => {
        let initialValues = {};

        if (data && data.hotelProfile) {
          let { operaProfile, patronBookingInfo } = data.hotelProfile;

          operaProfile = operaProfile || {};
          patronBookingInfo = patronBookingInfo || {};

          let stateValue =
            (operaProfile.address && operaProfile.address.stateProv) ||
            patronBookingInfo.stateProv;
          if (stateValue) {
            // Sanitize the data, since it may have trailing spaces.
            stateValue = stateValue.trim();
            if (stateValue in US_STATES_OPTIONS) {
              stateValue = {
                value: stateValue,
                label: US_STATES_OPTIONS[stateValue]
              };
            } else if (Object.values(US_STATES_OPTIONS).includes(stateValue)) {
              const stateAbbr = Object.keys(US_STATES_OPTIONS).find(
                key => US_STATES_OPTIONS[key] === stateValue
              );
              stateValue = { value: stateAbbr, label: stateValue };
            }
          }

          initialValues = {
            firstName: operaProfile.firstName || patronBookingInfo.firstName,
            lastName: operaProfile.lastName || patronBookingInfo.lastName,
            email: operaProfile.email || patronBookingInfo.email,
            phone:
              (operaProfile.phone && operaProfile.phone.replace(/\D*/g, '')) ||
              (patronBookingInfo.mobilePhone &&
                patronBookingInfo.mobilePhone.replace(/\D*/g, '')) ||
              (patronBookingInfo.homePhone &&
                patronBookingInfo.homePhone.replace(/\D*/g, '')),
            address:
              (operaProfile.address && operaProfile.address.addressLine) ||
              patronBookingInfo.addressLine1 +
                (patronBookingInfo.addressLine2
                  ? ` ${patronBookingInfo.addressLine2}`
                  : ''),
            city:
              (operaProfile.address && operaProfile.address.cityName) ||
              patronBookingInfo.city,
            state: stateValue,
            postalCode:
              (operaProfile.address &&
                operaProfile.address.postalCode &&
                operaProfile.address.postalCode.replace(/\D*/g, '')) ||
              (patronBookingInfo.postalCode &&
                patronBookingInfo.postalCode.replace(/\D*/g, ''))
          };
        }

        return (
          <React.Fragment>
            {loading && !skipQuery && (
              <SpinnerContainer>
                <FontAwesomeIcon icon="spinner" size="5x" spin />
              </SpinnerContainer>
            )}
            {error && (
              <ErrorModal
                property={property}
                isOpen={true}
                error={error}
                page={page}
              />
            )}
            {bookingSource === HOST_BOOKING ? (
              <HostBookingOptions
                firstName={initialValues.firstName}
                lastName={initialValues.lastName}
                initialValues={initialValues}
                property={property}
                isAdmin={isAdmin}
                bookingSource={bookingSource}
                enableReinitialize
                keepDirtyOnReinitialize
                page={page}
                {...props}
              />
            ) : (
              <GuestInformation
                loading={loading}
                error={error}
                data={data}
                initialValues={initialValues}
                property={property}
                isAdmin={isAdmin}
                isWaitListBooking={isWaitListBooking}
                bookingSource={bookingSource}
                enableReinitialize
                keepDirtyOnReinitialize
                {...props}
              />
            )}
          </React.Fragment>
        );
      }}
    </Query>
  );
}

const mapStateToProps = createStructuredSelector({
  submittedAccount: submittedAccountSelector,
  property: propertySelector,
  selectedRoom: selectedRoomSelector,
  isAdmin: isAdminSelector,
  bookingSource: bookingSourceSelector,
  guestInfo: guestInfoSelector,
  isWaitListBooking: isWaitListBookingSelector
});

function mapDispatchToProps(dispatch) {
  return {};
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GuestInformationContainer);
